/**
 * G2eGente - Grades de Treinamento por Setor
 * Organização de treinamentos por departamento + Análise de vínculo cargo-descrição
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { trainingGridsAPI, trainingsAPI, cargoMatchingAPI, jobPositionsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Loader2,
  Plus,
  Building2,
  GraduationCap,
  Clock,
  AlertTriangle,
  CheckCircle,
  Link2,
  Link2Off,
  Edit,
  Trash2,
  ChevronRight,
  Calendar,
  Users,
  FileText,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const PERIODICIDADES = {
  'mensal': 'Mensal',
  'trimestral': 'Trimestral',
  'semestral': 'Semestral',
  'anual': 'Anual'
};

export default function GradesTreinamento() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('grades');
  
  // Grades state
  const [grids, setGrids] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [positions, setPositions] = useState([]);
  const [setores, setSetores] = useState([]);
  
  // Cargo matching state
  const [cargoAnalysis, setCargoAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  
  // Modal states
  const [showGridModal, setShowGridModal] = useState(false);
  const [editingGrid, setEditingGrid] = useState(null);
  const [showVinculoModal, setShowVinculoModal] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  
  // Form state
  const [gridForm, setGridForm] = useState({
    nome: '',
    descricao: '',
    setor: '',
    training_ids: [],
    cargos_aplicaveis: [],
    periodicidade: 'anual'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gridsRes, trainingsRes, positionsRes] = await Promise.all([
        trainingGridsAPI.list(),
        trainingsAPI.list(),
        jobPositionsAPI.getAll()
      ]);
      
      setGrids(gridsRes.data);
      setTrainings(trainingsRes.data);
      setPositions(positionsRes.data);
      
      // Extrair setores únicos das grades e criar lista
      const setoresUnicos = [...new Set(gridsRes.data.map(g => g.setor).filter(Boolean))];
      setSetores(setoresUnicos);
      
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchCargoAnalysis = async () => {
    try {
      setLoadingAnalysis(true);
      const res = await cargoMatchingAPI.analyze();
      setCargoAnalysis(res.data);
    } catch (error) {
      toast.error('Erro ao analisar vínculos');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'vinculos') {
      fetchCargoAnalysis();
    }
  }, [activeTab]);

  const handleSaveGrid = async () => {
    if (!gridForm.nome.trim() || !gridForm.setor.trim()) {
      toast.error('Nome e setor são obrigatórios');
      return;
    }
    if (gridForm.training_ids.length === 0) {
      toast.error('Selecione pelo menos um treinamento');
      return;
    }

    try {
      if (editingGrid) {
        await trainingGridsAPI.update(editingGrid.id, gridForm);
        toast.success('Grade atualizada');
      } else {
        await trainingGridsAPI.create(gridForm);
        toast.success('Grade criada');
      }
      
      setShowGridModal(false);
      resetGridForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDeleteGrid = async (grid) => {
    if (!confirm(`Excluir grade "${grid.nome}"?`)) return;
    
    try {
      await trainingGridsAPI.delete(grid.id);
      toast.success('Grade excluída');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleVincular = async (descricaoId) => {
    if (!selectedCargo) return;
    
    try {
      setVinculando(true);
      const res = await cargoMatchingAPI.vincular(selectedCargo.cargo, descricaoId);
      toast.success(res.data.message);
      setShowVinculoModal(false);
      setSelectedCargo(null);
      fetchCargoAnalysis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular');
    } finally {
      setVinculando(false);
    }
  };

  const resetGridForm = () => {
    setGridForm({
      nome: '',
      descricao: '',
      setor: '',
      training_ids: [],
      cargos_aplicaveis: [],
      periodicidade: 'anual'
    });
    setEditingGrid(null);
  };

  const openEditGrid = (grid) => {
    setEditingGrid(grid);
    setGridForm({
      nome: grid.nome,
      descricao: grid.descricao || '',
      setor: grid.setor,
      training_ids: grid.training_ids || [],
      cargos_aplicaveis: grid.cargos_aplicaveis || [],
      periodicidade: grid.periodicidade || 'anual'
    });
    setShowGridModal(true);
  };

  const openVinculoModal = (cargo) => {
    setSelectedCargo(cargo);
    setShowVinculoModal(true);
  };

  // Agrupar grades por setor
  const gridsBySetor = grids.reduce((acc, grid) => {
    const setor = grid.setor || 'Sem Setor';
    if (!acc[setor]) acc[setor] = [];
    acc[setor].push(grid);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="training-grids-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Grades de Treinamento</h1>
            <p className="text-slate-600">Organize treinamentos por setor e vincule cargos</p>
          </div>
          <Button 
            onClick={() => { resetGridForm(); setShowGridModal(true); }}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Grade
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="grades" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Grades por Setor
            </TabsTrigger>
            <TabsTrigger value="vinculos" className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Vínculo Cargo-Descrição
              {cargoAnalysis?.resumo?.total_sem_vinculo > 0 && (
                <Badge className="bg-amber-100 text-amber-700 ml-1">
                  {cargoAnalysis.resumo.total_sem_vinculo}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Grades por Setor */}
          <TabsContent value="grades" className="space-y-6">
            {Object.keys(gridsBySetor).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Nenhuma grade criada</h3>
                  <p className="text-slate-600 mb-4">
                    Crie grades de treinamento para organizar capacitações por setor
                  </p>
                  <Button onClick={() => { resetGridForm(); setShowGridModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Grade
                  </Button>
                </CardContent>
              </Card>
            ) : (
              Object.entries(gridsBySetor).map(([setor, gridsSetor]) => (
                <Card key={setor}>
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-slate-600" />
                      {setor}
                    </CardTitle>
                    <CardDescription>
                      {gridsSetor.length} grade(s) • {gridsSetor.reduce((acc, g) => acc + (g.trainings?.length || 0), 0)} treinamentos
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {gridsSetor.map((grid) => (
                        <div key={grid.id} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-medium text-slate-900">{grid.nome}</h4>
                                <Badge variant="outline">{PERIODICIDADES[grid.periodicidade]}</Badge>
                              </div>
                              {grid.descricao && (
                                <p className="text-sm text-slate-600 mb-3">{grid.descricao}</p>
                              )}
                              
                              {/* Treinamentos da grade */}
                              <div className="flex flex-wrap gap-2 mb-2">
                                {grid.trainings?.map((t) => (
                                  <div key={t.id} className="flex items-center gap-1 px-2 py-1 bg-cyan-50 rounded text-xs">
                                    <GraduationCap className="w-3 h-3 text-cyan-600" />
                                    <span className="text-cyan-700">{t.nome}</span>
                                    {t.carga_horaria && (
                                      <span className="text-cyan-500">({t.carga_horaria}h)</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Cargos aplicáveis */}
                              {grid.cargos_nomes?.length > 0 && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Users className="w-3 h-3" />
                                  Cargos: {grid.cargos_nomes.join(', ')}
                                </div>
                              )}
                              
                              {/* Totais */}
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <GraduationCap className="w-3 h-3" />
                                  {grid.trainings?.length || 0} treinamentos
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {grid.total_carga_horaria || 0}h carga total
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditGrid(grid)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDeleteGrid(grid)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Tab: Vínculo Cargo-Descrição */}
          <TabsContent value="vinculos" className="space-y-6">
            {loadingAnalysis ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : cargoAnalysis ? (
              <>
                {/* Stats */}
                <div className="grid gap-4 sm:grid-cols-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Users className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">
                            {cargoAnalysis.resumo.total_cargos_funcionarios}
                          </p>
                          <p className="text-sm text-slate-600">Cargos de Funcionários</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Link2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-600">
                            {cargoAnalysis.resumo.total_vinculados}
                          </p>
                          <p className="text-sm text-slate-600">Vinculados</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className={cargoAnalysis.resumo.total_sem_vinculo > 0 ? 'border-amber-200 bg-amber-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Link2Off className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-amber-600">
                            {cargoAnalysis.resumo.total_sem_vinculo}
                          </p>
                          <p className="text-sm text-slate-600">Sem Vínculo</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <FileText className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-purple-600">
                            {cargoAnalysis.resumo.total_descricoes}
                          </p>
                          <p className="text-sm text-slate-600">Descrições de Cargo</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Alerta se houver cargos sem vínculo */}
                {cargoAnalysis.cargos_sem_vinculo?.length > 0 && (
                  <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-5 h-5" />
                        Cargos Sem Descrição Vinculada
                      </CardTitle>
                      <CardDescription>
                        Estes cargos de funcionários não possuem uma descrição de cargo correspondente.
                        Os treinamentos vinculados às descrições não serão aplicados a esses funcionários.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Cargo no Cadastro</TableHead>
                            <TableHead className="text-center">Funcionários</TableHead>
                            <TableHead>Sugestões</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cargoAnalysis.cargos_sem_vinculo.map((item) => (
                            <TableRow key={item.cargo}>
                              <TableCell className="font-medium">{item.cargo}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline">{item.funcionarios.length}</Badge>
                              </TableCell>
                              <TableCell>
                                {item.sugestoes?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {item.sugestoes.map((s, idx) => (
                                      <Badge key={idx} className="bg-blue-100 text-blue-700 text-xs">
                                        {s.titulo}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-sm">Nenhuma sugestão</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openVinculoModal(item)}
                                >
                                  <Link2 className="w-4 h-4 mr-1" />
                                  Vincular
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Cargos vinculados */}
                {cargoAnalysis.cargos_vinculados?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        Cargos Vinculados Corretamente
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {cargoAnalysis.cargos_vinculados.map((item) => (
                          <div key={item.cargo} className="p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-slate-900">{item.cargo}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-600">
                              <ArrowRight className="w-3 h-3" />
                              {item.descricao_vinculada}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {item.funcionarios.length} funcionário(s)
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Descrições sem funcionários */}
                {cargoAnalysis.descricoes_sem_funcionarios?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-slate-700">
                        Descrições de Cargo Sem Funcionários
                      </CardTitle>
                      <CardDescription>
                        Estas descrições não têm funcionários com cargo correspondente
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {cargoAnalysis.descricoes_sem_funcionarios.map((d) => (
                          <Badge key={d.id} variant="outline" className="text-slate-600">
                            {d.titulo}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </TabsContent>
        </Tabs>

        {/* Modal: Nova/Editar Grade */}
        <Dialog open={showGridModal} onOpenChange={setShowGridModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingGrid ? 'Editar Grade' : 'Nova Grade de Treinamento'}
              </DialogTitle>
              <DialogDescription>
                Configure a grade de treinamentos para um setor
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="nome">Nome da Grade *</Label>
                  <Input
                    id="nome"
                    name="grid_name_unique"
                    autoComplete="off"
                    value={gridForm.nome}
                    onChange={(e) => setGridForm({ ...gridForm, nome: e.target.value })}
                    placeholder="Ex: Grade de Integração - Produção"
                  />
                </div>

                <div>
                  <Label htmlFor="setor">Setor *</Label>
                  <Input
                    id="setor"
                    name="setor_name_unique"
                    autoComplete="off"
                    value={gridForm.setor}
                    onChange={(e) => setGridForm({ ...gridForm, setor: e.target.value })}
                    placeholder="Ex: Produção, Administrativo"
                    list="setores-list"
                  />
                  <datalist id="setores-list">
                    {setores.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <Label>Periodicidade</Label>
                  <Select 
                    value={gridForm.periodicidade} 
                    onValueChange={(v) => setGridForm({ ...gridForm, periodicidade: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PERIODICIDADES).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={gridForm.descricao}
                    onChange={(e) => setGridForm({ ...gridForm, descricao: e.target.value })}
                    placeholder="Descrição opcional da grade..."
                    rows={2}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label>Treinamentos da Grade *</Label>
                  <div className="mt-2 border rounded-lg p-3 max-h-48 overflow-y-auto">
                    {trainings.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-slate-50 px-2 rounded">
                        <input
                          type="checkbox"
                          checked={gridForm.training_ids.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGridForm({
                                ...gridForm,
                                training_ids: [...gridForm.training_ids, t.id]
                              });
                            } else {
                              setGridForm({
                                ...gridForm,
                                training_ids: gridForm.training_ids.filter(id => id !== t.id)
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm flex-1">{t.nome}</span>
                        {t.requisitos_legais && (
                          <Badge variant="outline" className="text-xs text-red-600">{t.requisitos_legais}</Badge>
                        )}
                        {t.carga_horaria && (
                          <span className="text-xs text-slate-500">{t.carga_horaria}h</span>
                        )}
                      </label>
                    ))}
                    {trainings.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Nenhum treinamento cadastrado
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {gridForm.training_ids.length} treinamento(s) selecionado(s)
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <Label>Cargos Aplicáveis (opcional)</Label>
                  <div className="mt-2 border rounded-lg p-3 max-h-36 overflow-y-auto">
                    {positions.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-2 rounded">
                        <input
                          type="checkbox"
                          checked={gridForm.cargos_aplicaveis.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGridForm({
                                ...gridForm,
                                cargos_aplicaveis: [...gridForm.cargos_aplicaveis, p.id]
                              });
                            } else {
                              setGridForm({
                                ...gridForm,
                                cargos_aplicaveis: gridForm.cargos_aplicaveis.filter(id => id !== p.id)
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{p.titulo}</span>
                      </label>
                    ))}
                    {positions.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-2">
                        Nenhuma descrição de cargo cadastrada
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGridModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveGrid} className="bg-cyan-600 hover:bg-cyan-700">
                {editingGrid ? 'Atualizar' : 'Criar Grade'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Vincular Cargo */}
        <Dialog open={showVinculoModal} onOpenChange={setShowVinculoModal}>
          <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-base">Vincular Cargo a Descrição</DialogTitle>
              <DialogDescription className="text-xs">
                Selecione a descrição de cargo para vincular ao cargo &quot;{selectedCargo?.cargo}&quot;
              </DialogDescription>
            </DialogHeader>

            <div className="py-3 flex-1 overflow-hidden">
              <p className="text-xs text-slate-600 mb-3">
                <strong>{selectedCargo?.funcionarios?.length}</strong> funcionário(s) serão atualizados 
                com o novo cargo selecionado.
              </p>
              
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
                {positions.map((p) => (
                  <div 
                    key={p.id}
                    className="p-2 border rounded-lg hover:border-cyan-300 hover:bg-cyan-50 cursor-pointer transition-colors"
                    onClick={() => handleVincular(p.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{p.titulo}</p>
                        {p.descricao_sumaria && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.descricao_sumaria}</p>
                        )}
                      </div>
                      {vinculando ? (
                        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0 ml-2" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </div>
                ))}
                
                {positions.length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <FileText className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Nenhuma descrição de cargo cadastrada</p>
                    <p className="text-xs">Crie uma descrição em Engenharia de Cargo</p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowVinculoModal(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
